import React from 'react';
import ReactDOM from 'react-dom';
import { getItem, setItem } from './storage';
import { isUsingStandardChanter } from './globalSettings';
import { setPracticeReturnTo, peekPracticeReturnTo } from './practiceGate';
import './CommonDialogs.css';
import HCaptcha from '@hcaptcha/react-hcaptcha';

// Dialog type constants
export const DLG_CUSTOM_CHANTER_RECOMMENDED = 'dlg_custom_chanter_recommended';
export const DLG_KEYBOARD_CHANTER_INFO = 'dlg_keyboard_chanter_info';
export const DLG_MICROPHONE_PROBLEM = 'dlg_microphone_problem';
export const DLG_MICROPHONE_CONNECTION_ERROR = 'dlg_microphone_connection_error';
export const DLG_CHANTERSYNC_COMPLETE = 'dlg_chantersync_complete';
export const DLG_CHANTERSYNC_SAVED = 'dlg_chantersync_saved';

// Storage keys for tracking dialog visibility
const STORAGE_KEYS = {
  [DLG_CUSTOM_CHANTER_RECOMMENDED]: 'calibration_dialog_seen',
  [DLG_KEYBOARD_CHANTER_INFO]: 'keyboard_info_seen',
};

// Session storage keys for tracking dialog instances
const SESSION_KEYS = {
  [DLG_CUSTOM_CHANTER_RECOMMENDED]: 'dlg_custom_chanter_instance',
  [DLG_KEYBOARD_CHANTER_INFO]: 'dlg_keyboard_info_instance',
};

// Track active dialog instances to prevent duplicates
const activeDialogs = new Set();

// Function to get component-specific storage key
const getComponentStorageKey = (dialogType, componentName) => {
  const baseKey = STORAGE_KEYS[dialogType];
  return componentName ? `${baseKey}_${componentName}` : baseKey;
};

// Function to get component-specific session key
const getComponentSessionKey = (dialogType, componentName) => {
  const baseKey = SESSION_KEYS[dialogType];
  return componentName ? `${baseKey}_${componentName}` : baseKey;
};

// Component for the Custom Chanter Recommended dialog
const CustomChanterRecommendedDialog = ({ onClose, componentName }) => {
  const handleCalibrateClick = () => {
    // First close the dialog to clean up resources
    if (onClose) onClose();

    // Log for debugging
    console.log('[Dialog Debug] Navigating to ChanterSync from dialog - using absolute URL');

    // Use the full, absolute URL to bypass React Router
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/chantersync`;

    // Store a flag in localStorage (more persistent than sessionStorage)
    localStorage.setItem('_force_navigate_to_chantersync', 'true');

    // Set the active section directly in localStorage
    localStorage.setItem('active_section', 'chanter-sync');

    // Replace current location with absolute URL
    window.location.href = url;

    // Return false to prevent default behavior and stop event propagation
    return false;
  };

  const handleContinueClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Custom Chanter Recommended</h3>
        <p>
          You are currently using a standard chanter profile. For the best experience, we recommend
          creating and calibrating a custom chanter that matches your specific instrument.
        </p>
        <p>
          Custom chanters provide more accurate note detection and better performance during games
          and practice sessions by accounting for your chanter's unique tuning.
        </p>
        <p>Would you like to set up a custom chanter now?</p>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={handleCalibrateClick}>
            Set Up Custom Chanter
          </button>
          <button className="calibration-dialog-button secondary" onClick={handleContinueClick}>
            Continue With Standard
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the Keyboard Chanter Info dialog
const KeyboardChanterInfoDialog = ({ onClose, componentName }) => {
  const handleOkClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Keyboard Chanter Selected</h3>
        <p>
          You have selected the Keyboard Chanter, which allows you to use your computer keyboard to
          simulate playing a bagpipe chanter.
        </p>
        <p>
          This feature is intended for demonstration purposes or when you don't have access to a
          real chanter. For the best experience with a real bagpipe chanter, we recommend creating a
          custom chanter profile.
        </p>
        <p>Use the following keys:</p>
        <div className="keyboard-layout">
          <div>
            <strong>g</strong>: Low G
          </div>
          <div>
            <strong>a</strong>: Low A
          </div>
          <div>
            <strong>b</strong>: B
          </div>
          <div>
            <strong>c</strong>: C
          </div>
          <div>
            <strong>d</strong>: D
          </div>
          <div>
            <strong>e</strong>: E
          </div>
          <div>
            <strong>f</strong>: F
          </div>
          <div>
            <strong>G</strong>: High G
          </div>
          <div>
            <strong>A</strong>: High A
          </div>
        </div>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={handleOkClick}>
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the Microphone Problem dialog
const MicrophoneProblemDialog = ({ onClose, message, errorCode }) => {
  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Microphone Access Problem</h3>
        {errorCode && (
          <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: 8 }}>
            Error: {errorCode}
          </div>
        )}
        <div style={{ marginBottom: 16 }}>{message}</div>
        <div style={{ marginBottom: 16, color: '#fff' }}>
          We will guide you through fixing this problem.
        </div>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the Microphone Connection Error dialog
const MicrophoneConnectionErrorDialog = ({ onClose }) => {
  const handleYes = () => {
    if (onClose) onClose();
    // Use absolute URL to ensure navigation works everywhere
    window.location.href = '/microphone-help';
  };
  const handleNo = () => {
    if (onClose) onClose();
  };
  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Microphone Connection Issue</h3>
        <p>We are having trouble connecting to your microphone. Would you like to troubleshoot?</p>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={handleYes}>
            Yes
          </button>
          <button className="calibration-dialog-button secondary" onClick={handleNo}>
            No
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the ChanterSync completion dialog
const ChanterSyncCompleteDialog = ({ onClose }) => {
  const handleOkClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Chanter Sync Complete</h3>
        <p>Chanter Sync complete. Click Save Notes if you are happy with the calibration.</p>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={handleOkClick}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the ChanterSync saved dialog
const ChanterSyncSavedDialog = ({ onClose }) => {
  const handleTestClick = () => {
    if (onClose) onClose();

    // Pitch games require Chanter Test once after each calibration change
    if (!peekPracticeReturnTo()) {
      setPracticeReturnTo('/games');
    }
    localStorage.setItem('active_section', 'chanter-test');
    window.location.href = '/chanter-test';

    return false;
  };

  const handleCancelClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog">
        <h3>Chanter Saved</h3>
        <p>
          Frequencies saved. Digital model profiles are ready for games — Chanter Test is optional
          if you want to double-check.
        </p>
        <div className="calibration-dialog-buttons">
          <button className="calibration-dialog-button primary" onClick={handleCancelClick}>
            OK
          </button>
          <button className="calibration-dialog-button secondary" onClick={handleTestClick}>
            Open Chanter Test
          </button>
        </div>
      </div>
    </div>
  );
};

// Dialog container that handles mounting/unmounting
const DialogContainer = ({ dialogType, onClose, componentName, message, errorCode }) => {
  // Select the appropriate dialog component based on type
  const renderDialog = () => {
    switch (dialogType) {
      case DLG_CUSTOM_CHANTER_RECOMMENDED:
        return <CustomChanterRecommendedDialog onClose={onClose} componentName={componentName} />;
      case DLG_KEYBOARD_CHANTER_INFO:
        return <KeyboardChanterInfoDialog onClose={onClose} componentName={componentName} />;
      case DLG_MICROPHONE_PROBLEM:
        return (
          <MicrophoneProblemDialog onClose={onClose} message={message} errorCode={errorCode} />
        );
      case DLG_MICROPHONE_CONNECTION_ERROR:
        return <MicrophoneConnectionErrorDialog onClose={onClose} />;
      case DLG_CHANTERSYNC_COMPLETE:
        return <ChanterSyncCompleteDialog onClose={onClose} />;
      case DLG_CHANTERSYNC_SAVED:
        return <ChanterSyncSavedDialog onClose={onClose} />;
      default:
        return null;
    }
  };

  return renderDialog();
};

// Main function to show a dialog
export const dlg_ShowDialog = (dialogType, options = {}) => {
  // Check if the user has already seen this dialog (if tracking is enabled)
  const {
    trackSeen = true,
    forceShow = false,
    componentName = null,
    message = '',
    errorCode = '',
  } = options;

  console.log(
    `[Dialog Debug] dlg_ShowDialog called for ${dialogType} in component ${componentName || 'unknown'}`,
    {
      trackSeen,
      forceShow,
    }
  );

  // Create a unique ID for this dialog instance
  const dialogId = `${dialogType}_${componentName || 'global'}`;

  // Check if this dialog is already active
  if (activeDialogs.has(dialogId)) {
    console.log(`[Dialog Debug] Dialog ${dialogId} is already active, not showing duplicate`);
    return false;
  }

  // Check session storage to prevent multiple dialogs in the same session
  const sessionKey = getComponentSessionKey(dialogType, componentName);
  const sessionInstance = getItem(sessionKey, false);

  console.log(
    `[Dialog Debug] Session key check: ${sessionKey}, instance exists: ${sessionInstance}`
  );

  if (sessionInstance && !forceShow) {
    console.log(
      `[Dialog Debug] Dialog ${dialogId} already shown in this session, not showing again`
    );
    return false;
  }

  // Mark dialog as shown in this session
  setItem(sessionKey, true);
  console.log(`[Dialog Debug] Marked session key ${sessionKey} as shown`);

  // Check if dialog has been permanently dismissed (unless forced)
  if (trackSeen && !forceShow) {
    const storageKey = getComponentStorageKey(dialogType, componentName);
    const hasSeenDialog = getItem(storageKey, false);
    console.log(
      `[Dialog Debug] Checking if dialog was seen before for key ${storageKey}: ${hasSeenDialog}`
    );

    if (hasSeenDialog) {
      console.log(`[Dialog Debug] Dialog ${dialogType} was already seen, not showing.`);
      return false; // Dialog was previously seen and dismissed
    }
  }

  // Create a div for the dialog
  const dialogRoot = document.createElement('div');
  dialogRoot.id = `dialog-${dialogType}${componentName ? `-${componentName}` : ''}`;
  document.body.appendChild(dialogRoot);
  console.log(`[Dialog Debug] Created dialog root element with ID ${dialogRoot.id}`);

  // Add to active dialogs set
  activeDialogs.add(dialogId);

  // Function to close and clean up the dialog
  const closeDialog = () => {
    // Mark dialog as seen if tracking is enabled
    if (trackSeen) {
      const storageKey = getComponentStorageKey(dialogType, componentName);
      setItem(storageKey, true);
      console.log(`[Dialog Debug] Marked dialog as seen for key ${storageKey}`);
    }

    // Remove from active dialogs set
    activeDialogs.delete(dialogId);
    console.log(`[Dialog Debug] Removed dialog ${dialogId} from active dialogs`);

    // Remove the dialog from DOM
    console.log(`[Dialog Debug] Cleaning up dialog element`);
    ReactDOM.unmountComponentAtNode(dialogRoot);
    if (dialogRoot.parentNode) {
      dialogRoot.parentNode.removeChild(dialogRoot);
    }
  };

  // Render the dialog
  console.log(`[Dialog Debug] Rendering dialog ${dialogType}`);
  try {
    ReactDOM.render(
      <DialogContainer
        dialogType={dialogType}
        onClose={closeDialog}
        componentName={componentName}
        message={message}
        errorCode={errorCode}
      />,
      dialogRoot
    );
    console.log(`[Dialog Debug] Dialog ${dialogType} rendered successfully`);
    return true; // Dialog was shown
  } catch (error) {
    console.error(`[Dialog Debug] Error rendering dialog:`, error);
    // Clean up if rendering fails
    if (dialogRoot.parentNode) {
      dialogRoot.parentNode.removeChild(dialogRoot);
    }
    // Clear session flag if rendering fails
    setItem(sessionKey, false);
    // Remove from active dialogs
    activeDialogs.delete(dialogId);
    return false;
  }
};

/**
 * Function to check and show chanter recommendation dialog
 * More consistent across components
 */
export const showChanterRecommendationIfNeeded = (
  selectedChanter,
  chanters,
  componentName,
  forceShow = false
) => {
  // Use the UUID-based check from globalSettings instead of name-based check
  // This is more reliable than checking by name
  const isUsingCustomChanter = !isUsingStandardChanter();

  // Only show dialog if NOT using a custom chanter
  if (!isUsingCustomChanter) {
    console.log(`[Dialog Debug] Custom chanter not in use, showing dialog for ${componentName}`);

    // For components that need to force the dialog, respect that
    const shouldForce = forceShow === true;
    console.log(`[Dialog Debug] Should force dialog: ${shouldForce}`);

    return dlg_ShowDialog(DLG_CUSTOM_CHANTER_RECOMMENDED, {
      componentName,
      forceShow: shouldForce,
    });
  }

  return false;
};

/**
 * Reset all dialog "seen" flags for testing purposes
 * @param {string} [componentName] - Optional component name to reset only that component's dialogs
 */
export function resetDialogState(componentName = null) {
  console.log(
    `[Dialog Debug] Resetting dialog state ${componentName ? 'for ' + componentName : 'for all components'}`
  );

  const componentsToReset = ['chantertest', 'battle', 'blaster', 'flashcard'];

  Object.keys(STORAGE_KEYS).forEach(dialogType => {
    if (componentName) {
      // Reset specific component dialog
      const key = getComponentStorageKey(dialogType, componentName);
      setItem(key, false);

      // Reset session key
      const sessionKey = getComponentSessionKey(dialogType, componentName);
      setItem(sessionKey, false);
    } else {
      // Reset all components for this dialog type
      componentsToReset.forEach(comp => {
        const key = getComponentStorageKey(dialogType, comp);
        setItem(key, false);
        console.log(`[Dialog Debug] Reset ${key} to false`);

        // Reset session keys
        const sessionKey = getComponentSessionKey(dialogType, comp);
        setItem(sessionKey, false);
        console.log(`[Dialog Debug] Reset session key ${sessionKey} to false`);
      });

      // Also reset the global keys
      const globalKey = STORAGE_KEYS[dialogType];
      setItem(globalKey, false);
      console.log(`[Dialog Debug] Reset ${globalKey} to false`);

      const globalSessionKey = SESSION_KEYS[dialogType];
      setItem(globalSessionKey, false);
      console.log(`[Dialog Debug] Reset session key ${globalSessionKey} to false`);
    }
  });

  // Clear active dialogs set
  activeDialogs.clear();

  return true;
}

/**
 * Force a dialog to show regardless of whether it's been seen before
 * @param {string} dialogType - Dialog type constant
 * @param {string} [componentName=null] - Optional component name
 */
export function forceShowDialog(dialogType, componentName = null) {
  console.log(
    `[Dialog Debug] forceShowDialog called for ${dialogType} in ${componentName || 'unknown'}`
  );

  // Remove any existing dialog elements with the same ID to ensure we can show a new one
  const existingDialogId = `dialog-${dialogType}${componentName ? `-${componentName}` : ''}`;
  const existingElement = document.getElementById(existingDialogId);
  if (existingElement) {
    console.log(`[Dialog Debug] Removing existing dialog element with ID ${existingDialogId}`);
    ReactDOM.unmountComponentAtNode(existingElement);
    if (existingElement.parentNode) {
      existingElement.parentNode.removeChild(existingElement);
    }
  }

  // Clear from active dialogs set
  const dialogId = `${dialogType}_${componentName || 'global'}`;
  activeDialogs.delete(dialogId);

  // Reset session key to allow showing dialog again
  const sessionKey = getComponentSessionKey(dialogType, componentName);
  setItem(sessionKey, false);

  // Reset permanent dismissal
  const storageKey = getComponentStorageKey(dialogType, componentName);
  setItem(storageKey, false);

  // Show dialog with forceShow=true
  return dlg_ShowDialog(dialogType, {
    componentName,
    forceShow: true,
  });
}

/**
 * Clear all dialog instances when navigating away
 */
export function clearDialogInstances() {
  // Reset all session flags
  Object.keys(SESSION_KEYS).forEach(dialogType => {
    const globalSessionKey = SESSION_KEYS[dialogType];
    setItem(globalSessionKey, false);

    // Also clear component-specific flags
    ['chantertest', 'battle', 'blaster', 'flashcard'].forEach(comp => {
      const sessionKey = getComponentSessionKey(dialogType, comp);
      setItem(sessionKey, false);
    });
  });

  // Clear active dialogs set
  activeDialogs.clear();

  // Remove any lingering dialog elements
  document.querySelectorAll('[id^="dialog-"]').forEach(element => {
    ReactDOM.unmountComponentAtNode(element);
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });
}

/**
 * Create a dialog directly in the DOM without React
 * For fallback when React-based dialogs fail
 */
export function createDirectDialog(componentName = null) {
  console.log(`[Dialog Debug] Creating direct DOM dialog for ${componentName || 'unknown'}`);

  // Handle existing dialogs first
  const existing = document.querySelectorAll('.calibration-dialog-overlay');
  existing.forEach(el => {
    try {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    } catch (error) {
      console.error('[Dialog Debug] Error cleaning up existing dialog:', error);
    }
  });

  // Mark as seen to prevent future attempts
  const storageKey = getComponentStorageKey(DLG_CUSTOM_CHANTER_RECOMMENDED, componentName);
  const sessionKey = getComponentSessionKey(DLG_CUSTOM_CHANTER_RECOMMENDED, componentName);
  setItem(sessionKey, true);

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'calibration-dialog-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.right = '0';
  overlay.style.bottom = '0';
  overlay.style.background = 'rgba(0, 0, 0, 0.7)';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.zIndex = '1000';

  // Create dialog
  const dialog = document.createElement('div');
  dialog.className = 'calibration-dialog';
  dialog.style.background = '#2c2c2c';
  dialog.style.borderRadius = '8px';
  dialog.style.padding = '20px';
  dialog.style.maxWidth = '450px';
  dialog.style.width = '90%';
  dialog.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
  dialog.style.border = '1px solid rgba(255, 255, 255, 0.1)';

  // Add title
  const title = document.createElement('h3');
  title.textContent = 'Custom Chanter Recommended';
  title.style.margin = '0 0 15px';
  title.style.color = '#fff';
  title.style.fontSize = '1.3rem';
  title.style.textAlign = 'center';

  // Add content
  const content1 = document.createElement('p');
  content1.textContent =
    'You are currently using a standard chanter profile. For the best experience, we recommend creating and calibrating a custom chanter that matches your specific instrument.';
  content1.style.margin = '10px 0';
  content1.style.color = 'rgba(255, 255, 255, 0.9)';
  content1.style.lineHeight = '1.5';
  content1.style.fontSize = '1rem';
  content1.style.textAlign = 'center';

  const content2 = document.createElement('p');
  content2.textContent =
    "Custom chanters provide more accurate note detection and better performance during games and practice sessions by accounting for your chanter's unique tuning.";
  content2.style.margin = '10px 0';
  content2.style.color = 'rgba(255, 255, 255, 0.9)';
  content2.style.lineHeight = '1.5';
  content2.style.fontSize = '1rem';
  content2.style.textAlign = 'center';

  const content3 = document.createElement('p');
  content3.textContent = 'Would you like to set up a custom chanter now?';
  content3.style.margin = '10px 0';
  content3.style.color = 'rgba(255, 255, 255, 0.9)';
  content3.style.lineHeight = '1.5';
  content3.style.fontSize = '1rem';
  content3.style.textAlign = 'center';

  // Add buttons container
  const buttons = document.createElement('div');
  buttons.style.display = 'flex';
  buttons.style.justifyContent = 'center';
  buttons.style.gap = '10px';
  buttons.style.marginTop = '20px';

  // Calibrate button
  const calibrateBtn = document.createElement('button');
  calibrateBtn.textContent = 'Set Up Custom Chanter';
  calibrateBtn.style.padding = '10px 15px';
  calibrateBtn.style.borderRadius = '4px';
  calibrateBtn.style.border = 'none';
  calibrateBtn.style.fontSize = '0.9rem';
  calibrateBtn.style.cursor = 'pointer';
  calibrateBtn.style.minWidth = '120px';
  calibrateBtn.style.transition = 'all 0.2s ease';
  calibrateBtn.style.background = '#4caf50';
  calibrateBtn.style.color = 'white';

  calibrateBtn.onclick = () => {
    // Mark as seen
    setItem(storageKey, true);

    // Remove dialog
    try {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    } catch (error) {
      console.error('[Dialog Debug] Error removing dialog:', error);
    }

    // Navigate with absolute URL
    console.log('[Dialog Debug] Navigating to ChanterSync from direct dialog - using absolute URL');

    // Use the full, absolute URL to bypass React Router
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/chantersync`;

    // Store a flag in localStorage
    localStorage.setItem('_force_navigate_to_chantersync', 'true');

    // Set the active section directly in localStorage
    localStorage.setItem('active_section', 'chanter-sync');

    // Replace current location with absolute URL
    window.location.href = url;

    return false;
  };

  // Continue button
  const continueBtn = document.createElement('button');
  continueBtn.textContent = 'Continue With Standard';
  continueBtn.style.padding = '10px 15px';
  continueBtn.style.borderRadius = '4px';
  continueBtn.style.border = '1px solid rgba(255, 255, 255, 0.3)';
  continueBtn.style.fontSize = '0.9rem';
  continueBtn.style.cursor = 'pointer';
  continueBtn.style.minWidth = '120px';
  continueBtn.style.transition = 'all 0.2s ease';
  continueBtn.style.background = 'transparent';
  continueBtn.style.color = 'white';

  continueBtn.onclick = () => {
    // Mark as seen
    setItem(storageKey, true);

    // Remove dialog
    try {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    } catch (error) {
      console.error('[Dialog Debug] Error removing dialog:', error);
    }
  };

  // Assemble dialog
  buttons.appendChild(calibrateBtn);
  buttons.appendChild(continueBtn);

  dialog.appendChild(title);
  dialog.appendChild(content1);
  dialog.appendChild(content2);
  dialog.appendChild(content3);
  dialog.appendChild(buttons);

  overlay.appendChild(dialog);

  // Add to DOM
  try {
    document.body.appendChild(overlay);
    return true;
  } catch (error) {
    console.error('[Dialog Debug] Error appending dialog to body:', error);
    return false;
  }
}

/**
 * Function to show keyboard chanter info dialog when keyboard chanter is selected
 * @param {string} selectedChanter - The currently selected chanter
 * @param {string} componentName - The name of the component showing the dialog
 * @param {boolean} forceShow - Whether to force the dialog to show regardless of seen status
 * @returns {boolean} Whether the dialog was shown
 */
export const showKeyboardChanterInfoIfNeeded = (
  selectedChanter,
  componentName,
  forceShow = false
) => {
  console.log(
    `[Dialog Debug] Component: ${componentName}, Selected Chanter: ${selectedChanter}, Force Show: ${forceShow}`
  );

  // Check if the selected chanter is Keyboard
  const isKeyboardChanter = selectedChanter === 'Keyboard';

  console.log(`[Dialog Debug] Is keyboard chanter: ${isKeyboardChanter}`);

  // Show dialog if using keyboard chanter
  if (isKeyboardChanter) {
    console.log(`[Dialog Debug] Attempting to show keyboard info dialog for ${componentName}`);

    // For components that need to force the dialog, respect that
    const shouldForce = forceShow === true;
    console.log(`[Dialog Debug] Should force dialog: ${shouldForce}`);

    return dlg_ShowDialog(DLG_KEYBOARD_CHANTER_INFO, {
      componentName,
      forceShow: shouldForce,
    });
  }

  console.log(
    `[Dialog Debug] No need to show keyboard info dialog, using different chanter: ${selectedChanter}`
  );
  return false;
};

/**
 * Create a Keyboard Info dialog directly in the DOM without React
 * For fallback when React-based dialogs fail
 */
export function createKeyboardInfoDirectDialog(componentName = null) {
  console.log(
    `[Dialog Debug] Creating direct DOM keyboard info dialog for ${componentName || 'unknown'}`
  );

  // Handle existing dialogs first
  const existing = document.querySelectorAll('.calibration-dialog-overlay');
  existing.forEach(el => {
    try {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    } catch (error) {
      console.error('[Dialog Debug] Error cleaning up existing dialog:', error);
    }
  });

  // Mark as seen to prevent future attempts
  const storageKey = getComponentStorageKey(DLG_KEYBOARD_CHANTER_INFO, componentName);
  const sessionKey = getComponentSessionKey(DLG_KEYBOARD_CHANTER_INFO, componentName);
  setItem(sessionKey, true);

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'calibration-dialog-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.right = '0';
  overlay.style.bottom = '0';
  overlay.style.background = 'rgba(0, 0, 0, 0.7)';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.zIndex = '1000';

  // Create dialog
  const dialog = document.createElement('div');
  dialog.className = 'calibration-dialog';
  dialog.style.background = '#2c2c2c';
  dialog.style.borderRadius = '8px';
  dialog.style.padding = '20px';
  dialog.style.maxWidth = '450px';
  dialog.style.width = '90%';
  dialog.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
  dialog.style.border = '1px solid rgba(255, 255, 255, 0.1)';

  // Add title
  const title = document.createElement('h3');
  title.textContent = 'Keyboard Chanter Selected';
  title.style.margin = '0 0 15px';
  title.style.color = '#fff';
  title.style.fontSize = '1.3rem';
  title.style.textAlign = 'center';

  // Add content
  const content1 = document.createElement('p');
  content1.textContent =
    'You have selected the Keyboard Chanter, which allows you to use your computer keyboard to simulate playing a bagpipe chanter.';
  content1.style.margin = '10px 0';
  content1.style.color = 'rgba(255, 255, 255, 0.9)';
  content1.style.lineHeight = '1.5';
  content1.style.fontSize = '1rem';
  content1.style.textAlign = 'center';

  const content2 = document.createElement('p');
  content2.textContent =
    "This feature is intended for demonstration purposes or when you don't have access to a real chanter. For the best experience with a real bagpipe chanter, we recommend creating a custom chanter profile.";
  content2.style.margin = '10px 0';
  content2.style.color = 'rgba(255, 255, 255, 0.9)';
  content2.style.lineHeight = '1.5';
  content2.style.fontSize = '1rem';
  content2.style.textAlign = 'center';

  const content3 = document.createElement('p');
  content3.textContent = 'Use the following keys:';
  content3.style.margin = '10px 0';
  content3.style.color = 'rgba(255, 255, 255, 0.9)';
  content3.style.lineHeight = '1.5';
  content3.style.fontSize = '1rem';
  content3.style.textAlign = 'center';

  // Create keyboard layout
  const keyboardLayout = document.createElement('div');
  keyboardLayout.className = 'keyboard-layout';
  keyboardLayout.style.display = 'grid';
  keyboardLayout.style.gridTemplateColumns = 'repeat(3, 1fr)';
  keyboardLayout.style.gap = '8px';
  keyboardLayout.style.margin = '15px auto';
  keyboardLayout.style.background = 'rgba(0, 0, 0, 0.2)';
  keyboardLayout.style.padding = '12px';
  keyboardLayout.style.borderRadius = '6px';
  keyboardLayout.style.maxWidth = '300px';
  keyboardLayout.style.textAlign = 'left';

  // Add key mappings
  const keys = [
    { key: 'g', note: 'Low G' },
    { key: 'a', note: 'Low A' },
    { key: 'b', note: 'B' },
    { key: 'c', note: 'C' },
    { key: 'd', note: 'D' },
    { key: 'e', note: 'E' },
    { key: 'f', note: 'F' },
    { key: 'G', note: 'High G' },
    { key: 'A', note: 'High A' },
  ];

  keys.forEach(({ key, note }) => {
    const keyDiv = document.createElement('div');

    const keySpan = document.createElement('strong');
    keySpan.textContent = key;
    keySpan.style.color = '#4caf50';
    keySpan.style.fontSize = '1.1rem';
    keySpan.style.marginRight = '5px';
    keySpan.style.background = 'rgba(0, 0, 0, 0.3)';
    keySpan.style.padding = '2px 6px';
    keySpan.style.borderRadius = '4px';
    keySpan.style.display = 'inline-block';
    keySpan.style.minWidth = '18px';
    keySpan.style.textAlign = 'center';

    keyDiv.appendChild(keySpan);
    keyDiv.appendChild(document.createTextNode(`: ${note}`));
    keyDiv.style.padding = '5px';
    keyDiv.style.color = 'rgba(255, 255, 255, 0.9)';
    keyDiv.style.fontSize = '0.9rem';

    keyboardLayout.appendChild(keyDiv);
  });

  // Add buttons container
  const buttons = document.createElement('div');
  buttons.style.display = 'flex';
  buttons.style.justifyContent = 'center';
  buttons.style.gap = '10px';
  buttons.style.marginTop = '20px';

  // Got It button
  const okBtn = document.createElement('button');
  okBtn.textContent = 'Got It';
  okBtn.style.padding = '10px 15px';
  okBtn.style.borderRadius = '4px';
  okBtn.style.border = 'none';
  okBtn.style.fontSize = '0.9rem';
  okBtn.style.cursor = 'pointer';
  okBtn.style.minWidth = '120px';
  okBtn.style.transition = 'all 0.2s ease';
  okBtn.style.background = '#4caf50';
  okBtn.style.color = 'white';

  okBtn.onclick = () => {
    // Mark as seen
    setItem(storageKey, true);

    // Remove dialog
    try {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    } catch (error) {
      console.error('[Dialog Debug] Error removing dialog:', error);
    }
  };

  // Assemble dialog
  buttons.appendChild(okBtn);

  dialog.appendChild(title);
  dialog.appendChild(content1);
  dialog.appendChild(content2);
  dialog.appendChild(content3);
  dialog.appendChild(keyboardLayout);
  dialog.appendChild(buttons);

  overlay.appendChild(dialog);

  // Add to DOM
  try {
    document.body.appendChild(overlay);
    return true;
  } catch (error) {
    console.error('[Dialog Debug] Error appending dialog to body:', error);
    return false;
  }
}

export function EmailDialog({
  open,
  userEmail,
  recipientName,
  onSend,
  onCancel,
  title = 'Send Email',
  hcaptchaSiteKey,
}) {
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [email, setEmail] = React.useState(userEmail || '');
  const [captchaToken, setCaptchaToken] = React.useState(null);
  const hcaptchaRef = React.useRef();

  React.useEffect(() => {
    if (open) {
      setMessage('');
      setError('');
      setEmail(userEmail || '');
      setCaptchaToken(null);
      if (hcaptchaRef.current) hcaptchaRef.current.resetCaptcha();
    }
  }, [open, userEmail]);

  if (!open) return null;

  const handleSend = e => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Message cannot be empty.');
      return;
    }
    if (!userEmail) {
      if (!email.trim()) {
        setError('Email is required.');
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        setError('Please enter a valid email address.');
        return;
      }
    }
    if (!captchaToken) {
      setError('Please complete the hCaptcha.');
      return;
    }
    setError('');
    onSend(message, userEmail || email, captchaToken);
  };

  return (
    <div className="calibration-dialog-overlay">
      <div className="calibration-dialog" style={{ maxWidth: 420 }}>
        <h3 style={{ textAlign: 'center', marginBottom: 18 }}>{title}</h3>
        <form onSubmit={handleSend}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#b0e0b0', fontWeight: 500 }}>Your Email:</label>
            <input
              type="email"
              value={userEmail ? userEmail : email}
              onChange={e => {
                if (!userEmail) setEmail(e.target.value);
              }}
              readOnly={!!userEmail}
              required
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 4,
                border: '1px solid #444',
                background: userEmail ? '#222' : '#fff',
                color: userEmail ? '#fff' : '#222',
                marginTop: 4,
              }}
              placeholder="Enter your email address"
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#b0e0b0', fontWeight: 500 }}>Recipient Name:</label>
            <input
              type="text"
              value={recipientName}
              readOnly
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 4,
                border: '1px solid #444',
                background: '#222',
                color: '#fff',
                marginTop: 4,
              }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="email-message" style={{ color: '#b0e0b0', fontWeight: 500 }}>
              Message:
            </label>
            <textarea
              id="email-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              style={{
                width: '100%',
                minHeight: 90,
                padding: 8,
                borderRadius: 4,
                border: '1px solid #444',
                background: '#222',
                color: '#fff',
                marginTop: 4,
              }}
              placeholder="Type your message here..."
              required
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <HCaptcha sitekey={hcaptchaSiteKey} onVerify={setCaptchaToken} ref={hcaptchaRef} />
          </div>
          {error && <div style={{ color: '#ffb0b0', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              type="button"
              onClick={onCancel}
              className="calibration-dialog-button secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="calibration-dialog-button primary"
              disabled={!captchaToken}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
